import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { auth } from '@/js/firebase';
import { dgfReportService } from '@/services/dgfReportService';
import { logger } from '@/services/loggerService';

// Stale threshold in milliseconds (24 hours)
const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000;

export const useDGFReportStore = defineStore('dgfReport', () => {
  // State - counts for navbar
  const counts = ref({
    projects: 0,
    deliverables: 0,
    events: 0,
    missingPersons: 0,
    total: 0
  });
  const lastUpdated = ref(null);
  const initialized = ref(false);
  const loading = ref(false);
  const refreshing = ref(false);

  // State - full data arrays for DGF Report page
  const projectsData = ref([]);
  const deliverablesData = ref({
    workingOnItOverdue: [],
    internalReviewOverdue: [],
    clientReviewOverdue: [],
    stuckTooLong: [],
    dueSoon: [],
    pastDue: [],
    debug: []
  });
  const eventsData = ref([]);
  const missingPersonsData = ref([]);
  const dataLastUpdated = ref(null);

  // State - rules configuration
  const rules = ref([]);

  // Unsubscribe function for real-time listener
  let unsubscribe = null;

  // Computed
  const hasItems = computed(() => counts.value.total > 0);
  const dataLoaded = computed(() => dataLastUpdated.value !== null);

  /**
   * Check if the cached counts are stale (older than 24 hours)
   */
  function isStale(date) {
    if (!date) return true;
    const now = new Date();
    const lastUpdate = date instanceof Date ? date : new Date(date);
    return (now - lastUpdate) > STALE_THRESHOLD_MS;
  }

  /**
   * Check if the full data is stale
   */
  function isDataStale() {
    if (!dataLastUpdated.value) return true;
    const now = new Date();
    const lastUpdate = dataLastUpdated.value instanceof Date ? dataLastUpdated.value : new Date(dataLastUpdated.value);
    return (now - lastUpdate) > STALE_THRESHOLD_MS;
  }

  /**
   * Load full report data, using cache if fresh
   * @param {boolean} forceRefresh - Force refresh even if data is fresh
   * @returns {Promise<Object>} Full report data
   */
  async function loadFullReport(forceRefresh = false) {
    // If data is loaded and fresh, return cached data
    if (!forceRefresh && dataLoaded.value && !isDataStale()) {
      logger.info('[DGFReportStore] Returning cached full report data');
      return {
        projectsData: projectsData.value,
        deliverablesData: deliverablesData.value,
        eventsData: eventsData.value,
        missingPersonsData: missingPersonsData.value,
        counts: counts.value
      };
    }

    // Fetch fresh data
    try {
      loading.value = true;
      logger.info('[DGFReportStore] Loading full report data', { forceRefresh });

      const result = await dgfReportService.getCounts(forceRefresh);

      // Update all data refs
      projectsData.value = result.projectsData;
      deliverablesData.value = result.deliverablesData;
      eventsData.value = result.eventsData;
      missingPersonsData.value = result.missingPersonsData;
      rules.value = result.rules || [];
      counts.value = {
        projects: result.projects,
        deliverables: result.deliverables,
        events: result.events,
        missingPersons: result.missingPersons,
        total: result.total
      };
      dataLastUpdated.value = new Date();

      logger.info('[DGFReportStore] Full report data loaded', {
        projects: result.projects,
        deliverables: result.deliverables,
        events: result.events,
        missingPersons: result.missingPersons
      });

      return {
        projectsData: projectsData.value,
        deliverablesData: deliverablesData.value,
        eventsData: eventsData.value,
        missingPersonsData: missingPersonsData.value,
        counts: counts.value
      };
    } catch (error) {
      logger.error('[DGFReportStore] Failed to load full report data', error);
      throw error;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Refresh counts by fetching fresh data from Firestore
   * This recalculates all counts and updates the cache
   */
  async function refreshCounts() {
    if (refreshing.value) {
      logger.info('[DGFReportStore] Already refreshing, skipping');
      return;
    }

    // The subscription's last snapshot fires as the token is revoked on sign-out;
    // refreshing then runs three getDocs against no auth and logs three errors.
    if (!auth.currentUser) {
      logger.warn('[DGFReportStore] Skipping refresh - no authenticated user');
      return;
    }

    try {
      refreshing.value = true;
      logger.info('[DGFReportStore] Refreshing stale counts');

      // getCounts(true) fetches fresh data and clears rules cache
      const result = await dgfReportService.getCounts(true);

      // Also update the full data arrays and rules
      projectsData.value = result.projectsData;
      deliverablesData.value = result.deliverablesData;
      eventsData.value = result.eventsData;
      missingPersonsData.value = result.missingPersonsData;
      rules.value = result.rules || [];
      dataLastUpdated.value = new Date();

      logger.info('[DGFReportStore] Counts refreshed successfully');
    } catch (error) {
      logger.warn('[DGFReportStore] Failed to refresh counts', error);
    } finally {
      refreshing.value = false;
    }
  }

  /**
   * Initialize the store and subscribe to real-time updates
   */
  async function init() {
    if (initialized.value) {
      logger.info('[DGFReportStore] Already initialized');
      return;
    }

    logger.info('[DGFReportStore] Initializing with real-time subscription');

    // Subscribe to real-time updates from Firestore
    unsubscribe = dgfReportService.subscribeToCachedCounts(async (data) => {
      counts.value = data.counts;
      lastUpdated.value = data.lastUpdated;
      initialized.value = true;
      logger.info('[DGFReportStore] Received counts update', data.counts);

      // Check if data is stale and refresh if needed (only on first load)
      if (isStale(data.lastUpdated) && !refreshing.value) {
        logger.info('[DGFReportStore] Cached counts are stale, triggering refresh');
        await refreshCounts();
      }
    });
  }

  /**
   * Fetch cached counts once (without subscription)
   */
  async function fetchCachedCounts() {
    try {
      loading.value = true;
      const cached = await dgfReportService.getCachedCounts();

      if (cached) {
        counts.value = cached.counts;
        lastUpdated.value = cached.lastUpdated;
        initialized.value = true;
      }
    } catch (error) {
      logger.warn('[DGFReportStore] Failed to fetch cached counts', error);
    } finally {
      loading.value = false;
    }
  }

  /**
   * Cleanup subscription
   */
  function cleanup() {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
      logger.info('[DGFReportStore] Cleaned up subscription');
    }
    initialized.value = false;
  }

  /**
   * Reset store state
   */
  function reset() {
    cleanup();
    counts.value = { projects: 0, deliverables: 0, events: 0, missingPersons: 0, total: 0 };
    lastUpdated.value = null;
    loading.value = false;
    projectsData.value = [];
    deliverablesData.value = {
      workingOnItOverdue: [],
      internalReviewOverdue: [],
      clientReviewOverdue: [],
      stuckTooLong: [],
      dueSoon: [],
      pastDue: [],
      debug: []
    };
    eventsData.value = [];
    missingPersonsData.value = [];
    rules.value = [];
    dataLastUpdated.value = null;
  }

  /**
   * Get a rule by its category
   * @param {string} category - The rule category
   * @returns {Object|null} The rule or null if not found
   */
  function getRuleByCategory(category) {
    return rules.value.find(r => r.category === category) || null;
  }

  return {
    // State - counts
    counts,
    lastUpdated,
    initialized,
    loading,
    refreshing,

    // State - full data
    projectsData,
    deliverablesData,
    eventsData,
    missingPersonsData,
    dataLastUpdated,

    // State - rules
    rules,

    // Computed
    hasItems,
    dataLoaded,

    // Actions
    init,
    fetchCachedCounts,
    loadFullReport,
    refreshCounts,
    cleanup,
    reset,
    getRuleByCategory
  };
});
